import { z } from 'zod';
import { calendarDateSchema, instantSchema, nonnegativeSafeIntegerSchema, semverSchema, sha256Schema, uuidSchema, } from './primitives.js';
import { routeCandidateSourceSchema, routeSeedSchema } from './route.js';
export const ROUTE_SCORING_VERSION = '1.0.1';
/**
 * One deterministic chronology contract for every route-learning consumer.
 * Service date is authoritative; stable structural keys break otherwise equal
 * occurrences without depending on persistence timestamps.
 */
export function compareRouteUseOrder(left, right) {
    return (compareAscii(left.service_date, right.service_date) ||
        compareAscii(left.actual_start ?? '', right.actual_start ?? '') ||
        compareAscii(left.shift_id, right.shift_id) ||
        left.segment_order - right.segment_order ||
        compareAscii(left.trip_segment_id, right.trip_segment_id));
}
export function normalizeRouteSeedForCurrentScoring(routeSeedInput) {
    const routeSeed = routeSeedSchema.parse(routeSeedInput);
    return routeSeedSchema.parse({
        ...routeSeed,
        route_scoring_version: ROUTE_SCORING_VERSION,
    });
}
export const routeOfferSourceSchema = z.enum(['history', 'template', 'network']);
const nullableContextTextSchema = z.string().min(1).max(256).nullable();
export const routeScoringContextSchema = z.strictObject({
    origin_location_id: uuidSchema,
    destination_location_id: uuidSchema,
    train_or_service_number: z.string().min(1).max(128).nullable(),
    work_type: z.string().min(1).max(128).nullable(),
    operator: z.string().min(1).max(256).nullable(),
    vehicle_ids: z.array(uuidSchema).max(16),
});
export const routeScoringObservationSchema = z.strictObject({
    // A shift's service date is the only V1 occurrence date.  Snapshot capture
    // and confirmation timestamps describe persistence, not when the trip happened.
    service_date: calendarDateSchema,
    // Kept as optional occurrence provenance for later scoring versions; V1 does
    // not turn it into a date because the service date is authoritative.
    actual_start: instantSchema.nullable(),
    origin_location_id: uuidSchema,
    destination_location_id: uuidSchema,
    train_or_service_number: z.string().min(1).max(128).nullable(),
    work_type: nullableContextTextSchema,
    operator: z.string().min(1).max(256).nullable(),
    vehicle_ids: z.array(uuidSchema).max(16),
});
export const routeScoringCandidateSchema = z
    .strictObject({
    candidate_id: z.string().min(1).max(512),
    route_variant_id: uuidSchema.nullable(),
    route_fingerprint_sha256: sha256Schema,
    candidate_source: routeCandidateSourceSchema,
    offer_source: routeOfferSourceSchema,
    origin_location_id: uuidSchema,
    destination_location_id: uuidSchema,
    explicit_confirmation_count: nonnegativeSafeIntegerSchema,
    use_count: nonnegativeSafeIntegerSchema,
    favorite: z.boolean(),
    default_for_context: z.boolean(),
    exact_variant_previously_confirmed: z.boolean(),
    observations: z.array(routeScoringObservationSchema).max(1_000_000),
})
    .superRefine((value, context) => {
    if (value.explicit_confirmation_count > value.use_count) {
        context.addIssue({
            code: 'custom',
            path: ['explicit_confirmation_count'],
            message: 'route scoring confirmations cannot exceed uses',
        });
    }
    if (value.explicit_confirmation_count > 0 && !value.exact_variant_previously_confirmed) {
        context.addIssue({
            code: 'custom',
            path: ['exact_variant_previously_confirmed'],
            message: 'a counted confirmation must identify the exact previously confirmed variant',
        });
    }
    if (value.offer_source === 'network' && value.exact_variant_previously_confirmed) {
        context.addIssue({
            code: 'custom',
            path: ['exact_variant_previously_confirmed'],
            message: 'a newly offered network candidate cannot claim prior exact confirmation',
        });
    }
    if (value.exact_variant_previously_confirmed && value.route_variant_id === null) {
        context.addIssue({
            code: 'custom',
            path: ['route_variant_id'],
            message: 'an exact previously confirmed candidate requires its stable route variant ID',
        });
    }
});
export const routeScoringInputSchema = z
    .strictObject({
    route_scoring_version: semverSchema.refine((value) => value === ROUTE_SCORING_VERSION, `route scoring version must be ${ROUTE_SCORING_VERSION}`),
    evaluated_at: instantSchema,
    evaluated_service_date: calendarDateSchema,
    context: routeScoringContextSchema,
    candidates: z.array(routeScoringCandidateSchema).max(100_000),
})
    .superRefine((value, context) => {
    const candidateIds = value.candidates.map((candidate) => candidate.candidate_id);
    if (new Set(candidateIds).size !== candidateIds.length) {
        context.addIssue({
            code: 'custom',
            path: ['candidates'],
            message: 'route scoring candidate IDs must be unique',
        });
    }
    const fingerprints = value.candidates.map((candidate) => candidate.route_fingerprint_sha256);
    if (new Set(fingerprints).size !== fingerprints.length) {
        context.addIssue({
            code: 'custom',
            path: ['candidates'],
            message: 'route scoring candidates must represent unique route fingerprints',
        });
    }
});
export const routeScoreBreakdownSchema = z.strictObject({
    direction: nonnegativeSafeIntegerSchema,
    frequency: nonnegativeSafeIntegerSchema,
    confirmation: nonnegativeSafeIntegerSchema,
    recency: nonnegativeSafeIntegerSchema,
    train_or_service_number: nonnegativeSafeIntegerSchema,
    work_type: nonnegativeSafeIntegerSchema,
    operator: nonnegativeSafeIntegerSchema,
    vehicle: nonnegativeSafeIntegerSchema,
    favorite: nonnegativeSafeIntegerSchema,
    default_for_context: nonnegativeSafeIntegerSchema,
});
export const routeScoringResultSchema = z.strictObject({
    route_scoring_version: z.literal(ROUTE_SCORING_VERSION),
    candidate_id: z.string().min(1).max(512),
    route_variant_id: uuidSchema.nullable(),
    route_fingerprint_sha256: sha256Schema,
    candidate_source: routeCandidateSourceSchema,
    offer_source: routeOfferSourceSchema,
    explicit_confirmation_count: nonnegativeSafeIntegerSchema,
    exact_variant_previously_confirmed: z.boolean(),
    score: nonnegativeSafeIntegerSchema,
    breakdown: routeScoreBreakdownSchema,
});
export const rankedRouteCandidateSchema = routeScoringResultSchema.extend({
    rank: z.number().int().safe().positive(),
});
export const routePreselectionResultSchema = z.discriminatedUnion('selected', [
    z.strictObject({
        selected: z.literal(true),
        candidate_id: z.string().min(1).max(512),
        route_scoring_version: z.literal(ROUTE_SCORING_VERSION),
        reason: z.literal('dominant_previously_confirmed_variant'),
    }),
    z.strictObject({
        selected: z.literal(false),
        candidate_id: z.null(),
        route_scoring_version: z.literal(ROUTE_SCORING_VERSION),
        reason: z.enum([
            'no_candidates',
            'winner_zero_score',
            'winner_not_previously_confirmed',
            'winner_below_confirmation_floor',
            'winner_is_new_network_candidate',
            'runner_up_too_close',
        ]),
    }),
]);
const WEIGHTS = {
    direction: 4_000,
    use: 100,
    confirmation: 300,
    favorite: 1_500,
    defaultForContext: 500,
    trainObservation: 240,
    workTypeObservation: 180,
    operatorObservation: 180,
    vehicleObservation: 220,
};
const CONTEXT_OBSERVATION_CAP = 10;
const GLOBAL_USE_CAP = 100;
const CONFIRMATION_CAP = 20;
export function scoreRouteCandidate(input) {
    instantSchema.parse(input.evaluated_at);
    const evaluatedServiceDate = calendarDateSchema.parse(input.evaluated_service_date);
    const context = normalizeContext(routeScoringContextSchema.parse(input.context));
    const candidate = routeScoringCandidateSchema.parse(input.candidate);
    const directionMatches = candidate.origin_location_id === context.origin_location_id &&
        candidate.destination_location_id === context.destination_location_id;
    const matchingDirectionObservations = candidate.observations.filter((observation) => observation.origin_location_id === context.origin_location_id &&
        observation.destination_location_id === context.destination_location_id);
    const matchCount = (pick) => Math.min(CONTEXT_OBSERVATION_CAP, matchingDirectionObservations.filter((observation) => pick(normalizeObservation(observation)))
        .length);
    const trainMatches = context.train_or_service_number === null
        ? 0
        : matchCount((observation) => observation.train_or_service_number === context.train_or_service_number);
    const workTypeMatches = context.work_type === null
        ? 0
        : matchCount((observation) => observation.work_type === context.work_type);
    const operatorMatches = context.operator === null
        ? 0
        : matchCount((observation) => observation.operator === context.operator);
    const contextVehicleIds = new Set(context.vehicle_ids);
    const vehicleMatches = contextVehicleIds.size === 0
        ? 0
        : matchCount((observation) => observation.vehicle_ids.some((vehicleId) => contextVehicleIds.has(vehicleId)));
    const breakdown = routeScoreBreakdownSchema.parse(directionMatches
        ? {
            direction: WEIGHTS.direction,
            frequency: Math.min(candidate.use_count, GLOBAL_USE_CAP) * WEIGHTS.use,
            confirmation: Math.min(candidate.explicit_confirmation_count, CONFIRMATION_CAP) *
                WEIGHTS.confirmation,
            recency: recencyScore(matchingDirectionObservations, evaluatedServiceDate),
            train_or_service_number: trainMatches * WEIGHTS.trainObservation,
            work_type: workTypeMatches * WEIGHTS.workTypeObservation,
            operator: operatorMatches * WEIGHTS.operatorObservation,
            vehicle: vehicleMatches * WEIGHTS.vehicleObservation,
            favorite: candidate.favorite ? WEIGHTS.favorite : 0,
            default_for_context: candidate.default_for_context ? WEIGHTS.defaultForContext : 0,
        }
        : {
            direction: 0,
            frequency: 0,
            confirmation: 0,
            recency: 0,
            train_or_service_number: 0,
            work_type: 0,
            operator: 0,
            vehicle: 0,
            favorite: 0,
            default_for_context: 0,
        });
    const score = Object.values(breakdown).reduce((sum, value) => sum + value, 0);
    return routeScoringResultSchema.parse({
        route_scoring_version: ROUTE_SCORING_VERSION,
        candidate_id: candidate.candidate_id,
        route_variant_id: candidate.route_variant_id,
        route_fingerprint_sha256: candidate.route_fingerprint_sha256,
        candidate_source: candidate.candidate_source,
        offer_source: candidate.offer_source,
        explicit_confirmation_count: candidate.explicit_confirmation_count,
        exact_variant_previously_confirmed: candidate.exact_variant_previously_confirmed,
        score,
        breakdown,
    });
}
export function rankRouteCandidates(input) {
    const parsed = routeScoringInputSchema.parse(input);
    const results = parsed.candidates.map((candidate) => scoreRouteCandidate({
        evaluated_at: parsed.evaluated_at,
        evaluated_service_date: parsed.evaluated_service_date,
        context: parsed.context,
        candidate,
    }));
    results.sort((left, right) => right.score - left.score ||
        compareAscii(left.route_fingerprint_sha256, right.route_fingerprint_sha256) ||
        compareAscii(left.candidate_id, right.candidate_id));
    return results.map((result, index) => rankedRouteCandidateSchema.parse({ ...result, rank: index + 1 }));
}
export function selectPreselectedRoute(input) {
    const ranked = rankRouteCandidates(input);
    const winner = ranked[0];
    if (winner === undefined)
        return noSelection('no_candidates');
    if (winner.score === 0)
        return noSelection('winner_zero_score');
    if (winner.offer_source === 'network') {
        return noSelection('winner_is_new_network_candidate');
    }
    if (!winner.exact_variant_previously_confirmed) {
        return noSelection('winner_not_previously_confirmed');
    }
    if (winner.explicit_confirmation_count < 3) {
        return noSelection('winner_below_confirmation_floor');
    }
    const runnerUpScore = ranked[1]?.score ?? 0;
    if (runnerUpScore > 0 && BigInt(winner.score) * 100n < BigInt(runnerUpScore) * 120n) {
        return noSelection('runner_up_too_close');
    }
    return routePreselectionResultSchema.parse({
        selected: true,
        candidate_id: winner.candidate_id,
        route_scoring_version: ROUTE_SCORING_VERSION,
        reason: 'dominant_previously_confirmed_variant',
    });
}
function noSelection(reason) {
    return routePreselectionResultSchema.parse({
        selected: false,
        candidate_id: null,
        route_scoring_version: ROUTE_SCORING_VERSION,
        reason,
    });
}
function normalizeContext(input) {
    return {
        ...input,
        vehicle_ids: uniqueAscii(input.vehicle_ids),
    };
}
function normalizeObservation(input) {
    return {
        ...input,
        vehicle_ids: uniqueAscii(input.vehicle_ids),
    };
}
function uniqueAscii(values) {
    return [...new Set(values)].sort(compareAscii);
}
function recencyScore(observations, evaluatedServiceDate) {
    const latestServiceDate = observations
        .map((observation) => observation.service_date)
        .sort(compareAscii)
        .at(-1);
    if (latestServiceDate === undefined)
        return 0;
    const ageMilliseconds = Date.parse(`${evaluatedServiceDate}T00:00:00.000Z`) -
        Date.parse(`${latestServiceDate}T00:00:00.000Z`);
    if (ageMilliseconds < 0)
        return 0;
    const ageDays = Math.floor(ageMilliseconds / 86_400_000);
    if (ageDays <= 7)
        return 2_000;
    if (ageDays <= 30)
        return 1_200;
    if (ageDays <= 90)
        return 600;
    if (ageDays <= 365)
        return 200;
    return 0;
}
function compareAscii(left, right) {
    return left < right ? -1 : left > right ? 1 : 0;
}
//# sourceMappingURL=route-scoring.js.map